import React from 'react';
import { Box, Container, Link, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SiteFooter from '../components/SiteFooter';

const MAROON = '#7b0011';
const CREAM = '#fdf3e4';
const TEXT = '#4f3b32';

const TermsOfUsePage: React.FC = () => (
  <Box sx={{ minHeight: '100%', backgroundColor: CREAM, py: { xs: 4, md: 7 } }}>
    <Container maxWidth="md">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5, md: 7 },
          border: '1px solid #eeddc7',
          borderRadius: 2,
          backgroundColor: '#fffdf9',
        }}
      >
        <Typography
          component="h1"
          sx={{
            color: MAROON,
            fontFamily: "'Georgia', 'Times New Roman', serif",
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '2.7rem' },
            mb: 1,
          }}
        >
          Terms of Use and Policies
        </Typography>
        <Typography sx={{ color: '#806b5e', mb: 4 }}>
          Maandvo.com
        </Typography>

        <Typography paragraph sx={{ color: TEXT, lineHeight: 1.8 }}>
          Welcome to Maandvo.com (the Platform), your dedicated matchmaking destination. This User Agreement outlines the legal conditions governing your access to our services and membership features. We reserve the right to modify or update these policies periodically, and we encourage you to review this page regularly for updates.
        </Typography>
        <Typography paragraph sx={{ color: TEXT, lineHeight: 1.8 }}>
          Maandvo.com operates as a social matchmaking community designed strictly for individual members to showcase their profiles and find compatible life partners. This service is intended solely for personal use and must not be used for commercial or profit-driven ventures. Our platform is exclusively for matchmaking and is not a general dating or casual contact website.
        </Typography>

        <Section title="Conditions of Use">
          <Typography paragraph>
            Accessing and using the Site indicates your formal acceptance of this Agreement. To register as a member, present your profile to others, and use our matchmaking features, you must read these conditions and complete our registration workflow.
          </Typography>
          <Typography>
            By using our service, you authorize the collection, processing, display, and use of your personal data necessary for platform operations. Revoking this consent requires you to close your account and stop using the service.
          </Typography>
        </Section>

        <Section title="Data Privacy and Protection">
          <Typography paragraph>
            Personal Information refers to any identifiable details, such as full names, residential or physical locations, email addresses, or alternative contact details. Users can freely explore the website without disclosing their identity or personal details.
          </Typography>
          <Typography paragraph>
            Safeguarding user confidentiality is a core priority for us. Collected details typically cover general matchmaking criteria, including age, height, gender, community, profession, location, family background, hobbies, and partner preferences. This information is curated to spark interest among potential life partners without exposing identity prematurely.
          </Typography>
          <Typography>
            Uploading photos is optional because privacy preferences vary.
          </Typography>
        </Section>

        <Section title="Minor Safety Guidelines">
          <Typography paragraph>
            Maandvo.com maintains a zero-tolerance stance toward the exploitation or endangerment of minors. Our services are restricted entirely to adults aged 18 and older. We do not intentionally gather data from anyone under 18; if we discover that a minor has submitted personal information, we will swiftly delete it and close the linked account.
          </Typography>
          <Typography>
            All participants are expected to behave respectfully and appropriately. Reports regarding underage users or inappropriate conduct toward minors will be addressed seriously and may involve legal authorities. Report violations to <Link href="mailto:support@maandvo.com" sx={{ color: MAROON, fontWeight: 700 }}>support@maandvo.com</Link>.
          </Typography>
        </Section>

        <Section title="Intellectual Property and Liability">
          <Typography paragraph>
            Unauthorized posting, sharing, or duplication of protected trademarks, copyrighted content, or proprietary assets without explicit written permission from the rightful owner is strictly prohibited.
          </Typography>
          <Typography>
            Maandvo.com disclaims liability for technical glitches, errors, service interruptions, deletion of data, transmission delays, communication failures, data theft, unauthorized access, or alterations involving member communications. Maandvo.com assumes no liability for losses or damages arising from platform usage or content transmissions. Members should never share sensitive financial details, such as bank accounts or credit cards, or transfer money to other users.
          </Typography>
        </Section>

        <Section title="Account Cancellation">
          <Typography>
            Either party may terminate a Maandvo.com membership at any time without providing a specific reason, and fees paid are non-refundable. Suspension or termination immediately revokes all rights to access platform services. Closing an account does not exempt users from responsibilities or duties incurred before termination.
          </Typography>
        </Section>

        <Section title="Member Interactions">
          <Typography>
            Members bear sole responsibility for their communications and interactions with other users on Maandvo.com. All engagements are conducted at your own risk and discretion, and Maandvo.com is not responsible for verifying the accuracy or truthfulness of information provided by other members.
          </Typography>
        </Section>

        <Section title="Billing Terms">
          <Typography paragraph>
            This Payment Policy serves as a binding electronic agreement governing financial transactions for membership packages on this website. The service provider may update these payment rules at its discretion, with changes taking effect immediately. Submitting a payment on Maandvo.com signifies your agreement to these financial terms.
          </Typography>
          <Typography paragraph>
            To secure a membership, users must register as a paid member and log into their registered account on Maandvo.com. Then navigate to the Make Payment section on the homepage and follow the on-screen directions to complete the transaction.
          </Typography>
        </Section>

        <Section title="Accepted Payment Modes">
          <Typography>
            Transactions can currently be completed using the QR Code displayed on the website or sent by the Maandvo team. Maandvo.com may introduce other payment facilities in the future, including credit cards, debit cards, net banking options restricted to Indian banks, and cheques or drafts payable via Indian banking institutions.
          </Typography>
        </Section>

        <Section title="Refund Policy">
          <Typography>
            All package purchases on Maandvo.com are final and non-refundable. Membership access terminates permanently alongside profile deletion or cancellation and cannot be reactivated. No reimbursements will be issued if an account is inactivated, deleted, or terminated.
          </Typography>
        </Section>

        <Section title="Fulfillment Policy">
          <Typography>
            All features and services are provided exclusively online, meaning no physical shipping or delivery fees apply.
          </Typography>
        </Section>

        <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #eeddc7' }}>
          <Link component={RouterLink} to="/" sx={{ color: MAROON, fontWeight: 700, textDecoration: 'none' }}>
            Return to Home
          </Link>
        </Box>
      </Paper>
    </Container>
    <SiteFooter />
  </Box>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <Box component="section" sx={{ mt: 4 }}>
    <Typography
      component="h2"
      sx={{
        color: MAROON,
        fontFamily: "'Georgia', 'Times New Roman', serif",
        fontWeight: 800,
        fontSize: { xs: '1.35rem', md: '1.55rem' },
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    <Box sx={{ color: TEXT, lineHeight: 1.8, '& p': { lineHeight: 1.8 } }}>
      {children}
    </Box>
  </Box>
);

export default TermsOfUsePage;
